<template>
  <div class="user-main-page">
    <!-- Birthday Modal -->
    <birthday-modal/>
    <!-- LOADING BOX -->
    <!--    <default-loading v-if="shouldShowLoading" :minHeight="'80vh'"></default-loading>-->
    <!-- Banner Slider -->
    <transition name="slider-fade">
      <div v-show="isSliderVisible" class="main-slider-wrapper">
        <anz-main-page-slider :advertisements="dashboard_item.advertisements || []" :loading="dashboardLoading"/>
      </div>
    </transition>

    <!-- MAIN CONTENT -->
    <div class="uk-container uk-container-large">
      <!-- TOP SECTION - Loads first -->
      <div class="home-top-section">
        <!-- SEARCH BAR -->
        <searching v-if="moduleList && Object.keys(moduleList).length" :loading="isFirstLoad && dashboardLoading"
                   @searchFor="searchResults">
          <template #top-right>
            <button v-if="dashboard_item.advertisements?.length" class="btn switch hide" type="button"
                    @click.prevent="bannerSlideUpDown">
              <i :class="isSliderVisible ? 'uil-minus-square-full' : 'uil-square-full'"/>
              {{ isSliderVisible ? $t('home.hide_ads') : $t('general.show_ads') }}
            </button>
          </template>
        </searching>

        <!-- DASHBOARD STATS -->
        <dashboard-stats :dashboard_item="dashboard_item" :module_list="moduleList"></dashboard-stats>
      </div>

      <div class="uk-child-width-expand" uk-grid>
        <div class="banner-container">
          <leaderboard-card :current_user="currentUser" :dashboard_item="dashboard_item"
                            :leaderBoardTitle="leaderBoardTitle" :leaderTableVisible="leaderTableVisible"
                            :leader_board="leader_board" :mobile-top-three="true"/>

          <social-wall-widget :current-user="currentUser"
                              :last-posts="last_posts"
                              :last-posts-loaded="!isFirstLoad"
                              :social-wall-title="socialWallTitle"
                              :social-wall-visible="socialWallVisible"
                              class="home-social-wall-widget"/>

        </div>

        <div class="uk-width-5-5@s" v-bind:class="{
          'uk-width-4-4@m': !socialWallVisible && !leaderTableVisible,
          'uk-width-3-4@m': socialWallVisible || leaderTableVisible
        }">
          <!-- Skeleton: Sadece ilk yüklemede göster -->
          <template v-if="isFirstLoad">
            <training-section-skeleton v-for="n in 3" :key="'skeleton_' + n" :show-rating="n === 2"/>
          </template>

          <!-- Content: Veriler yüklendikten sonra göster -->
          <template v-else>
            <template v-for="(item, index) in sortedModuleList">
              <!-- My Instructors -->
              <my-instructors-widget
                v-if="item.type === 'my_instructor' && myInstructors.length"
                :key="'my_instructors_' + (item.id || index)"
                :instructors="myInstructors"
                :item="item"
              />

              <!-- Collections -->
              <collection-card-slider
                v-else-if="item.type === 'collections'"
                :key="'collection_' + index"
                :collections="collections"
                :item="item"
              />

              <!-- Training Sections (bilinen modüller + custom widget'lar) -->
              <training-section v-else-if="getItemConfig(item)"
                                :key="'module_' + (item.id || item.type) + '_' + index"
                                :card-type="getItemConfig(item).cardType"
                                :icon="getItemConfig(item).icon"
                                :item="item"
                                :key-prefix="getItemConfig(item).keyPrefix"
                                :rating-star-points="getItemConfig(item).hasRating ? rating_star_points : undefined"
                                :route-name="getItemConfig(item).routeName"
                                :route-params="getItemConfig(item).routeParams"
                                :star-increment="getItemConfig(item).hasRating ? star_increment : undefined"
                                :trainings="getModuleData(item.type, item.id)"
                                :use-slider="getItemConfig(item).useSlider"
                                @provider-click="getItemConfig(item).isProvider ? openProviderDetails($event) : null"/>
            </template>
          </template>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Searching from "@/view/components/index/Searching.vue";
import SecureLS from "secure-ls";
import axios from "axios";
import AnzMainPageSlider from "@/view/components/slider/anz-main-page-slider/anz-main-page-slider.vue";

const ls = new SecureLS({ encodingType: 'aes', isCompression: true });
import { getSubCompanyId } from "@/core/services/store.js";
import BirthdayModal from "@/view/components/modal/BirthdayModal.vue";
import DashboardStats from "@/view/components/dashboard/DashboardStats.vue";
import LeaderboardCard from "@/view/components/card/LeaderboardCard.vue";
import SocialWallWidget from "@/view/User/Widgets/SocialWall/SocialWallWidget.vue";
import CollectionCardSlider from "@/view/components/slider/CollectionCardSlider.vue";
import TrainingSection from "./components/TrainingSection.vue";
import TrainingSectionSkeleton from "./components/TrainingSectionSkeleton.vue";
import MyInstructorsWidget from "./components/MyInstructorsWidget.vue";
import { dashboardModuleConfigMixin } from "@/mixins/dashboardModuleConfig";

// Static data — module-level constant (no per-instance allocation)
const RATING_STAR_POINTS = Object.freeze([23, 2, 14, 17, 0, 19, 10, 34, 7, 50, 23, 43, 38, 50, 36, 34, 46, 19, 31, 17]);
const STAR_INCREMENT = 0.1;

// Refresh'te store boş olduğu için widget listesi cache'den okunur:
// /api/widgets yanıtı gelene kadar doğru görünürlükle boyanır.
function getCachedDashboardModules() {
  try {
    const cached = ls.get("dashboardMenuList");
    return Array.isArray(cached?.dashboard) ? cached.dashboard : null;
  } catch (error) {
    return null;
  }
}

export default {
  name: "SoftinyaHomePage",
  mixins: [dashboardModuleConfigMixin],
  components: {
    AnzMainPageSlider,
    Searching,
    BirthdayModal,
    DashboardStats,
    LeaderboardCard,
    SocialWallWidget,
    CollectionCardSlider,
    TrainingSection,
    TrainingSectionSkeleton,
    MyInstructorsWidget
  },
  data() {
    return {
      // UI State - sadece component'e özel değişkenler
      leaderBoardTitle: this.$t('home.leaderboards'),
      socialWallTitle: this.$t('home.social_wall'),
      // Görünürlük modül listesinden gelir; bilinmeyen durumda kapalı başlar
      // (açık başlarsa kapalı widget bir kare görünüp kaybolur).
      leaderTableVisible: false,
      socialWallVisible: false,
      isSliderVisible: localStorage.getItem('isSliderVisible') !== 'false',
      isLoading: true,
      dashboardLoading: true,
      isFirstLoad: true,
      filters: { search: null },
      rating_star_points: RATING_STAR_POINTS,
      star_increment: STAR_INCREMENT,
    };
  },
  computed: {
    // Store'dan direkt computed - template'de aynı isimlerle kullanılıyor
    dashboard_item() {
      return this.$store.getters['dashboard/dashboardItem'] || {};
    },
    moduleList() {
      return this.$store.getters['dashboard/moduleList'] || {};
    },
    assigned_educations() {
      return this.$store.getters['dashboard/assignedEducations'] || [];
    },
    highlight_educations() {
      return this.$store.getters['dashboard/highlightEducations'] || [];
    },
    popular_educations() {
      return this.$store.getters['dashboard/popularEducations'] || [];
    },
    expired_educations() {
      return this.$store.getters['dashboard/expiredEducations'] || [];
    },
    new_trainings() {
      return this.$store.getters['dashboard/newTrainings'] || [];
    },
    optionalTrainings() {
      return this.$store.getters['dashboard/optionalTrainings'] || [];
    },
    competency_educations() {
      return this.$store.getters['dashboard/competencyEducations'] || [];
    },
    collections() {
      return (this.$store.getters['dashboard/collections'] || []).slice(0, 10);
    },
    myInstructors() {
      return this.$store.getters['dashboard/myInstructors'] || [];
    },
    leader_board() {
      return this.$store.getters['dashboard/leaderBoard'] || {};
    },
    last_posts() {
      return this.$store.getters['dashboard/lastPosts'] || [];
    },
    udemy_contents() {
      return this.$store.getters['dashboard/udemyContents'] || [];
    },
    coursera_contents() {
      return this.$store.getters['dashboard/courseraContents'] || [];
    },
    aeroclass_contents() {
      return this.$store.getters['dashboard/aeroclassContents'] || [];
    },
    linkedin_contents() {
      return this.$store.getters['dashboard/linkedinContents'] || [];
    },
    harvard_contents() {
      return this.$store.getters['dashboard/harvardContents'] || [];
    },
    widget_assignments() {
      return this.$store.getters['dashboard/widgetAssignments'] || {};
    },
    // Yardımcı computed'lar
    currentUser() {
      return this.authUser || {};
    },
    sortedModuleList() {
      return this.moduleList?.dashboard?.filter(item => {
        if (item.status !== 1) return false;
        if (item.type === 'leader_table' || item.type === 'social_wall') return false;
        return true;
      }) || [];
    },
    socialPostVisible() {
      return this.socialWallVisible;
    },
    hasStoreData() {
      return this.$store.getters['dashboard/hasInitialData'];
    },
  },
  methods: {
    bannerSlideUpDown() {
      this.isSliderVisible = !this.isSliderVisible;
      localStorage.setItem('isSliderVisible', this.isSliderVisible);
    },

    searchResults(val) {
      let search = val.trim();
      if (this.$route.name !== 'User.search') {
        this.$router.push({ name: 'User.search', query: { search: search } });
      }
    },

    openProviderDetails(result) {
      axios.get(process.env.VUE_APP_BACKEND_APP_URL + "/api/provider-content-show/" + result.provider + "/" + result.id, {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + this.secureLocalStorage("token"),
          Company: getSubCompanyId?.value,
        },
      }).then((response) => {
        if (response.data.status) {
          this.$router.push({ name: "user.assignment-detail", params: { id: response.data.assignment_id } });
        }
      });
    },

    // Ana sayfa başlatma
    initializePage() {
      if (this.hasStoreData) {
        // Store'da veri VAR - hemen göster, arka planda güncelle
        this.isFirstLoad = false;
        this.dashboardLoading = false;
        this.isLoading = false;
        this.setupDashboardModules();
        this.refreshDataInBackground();
      } else {
        // Store'da veri YOK - skeleton göster, verileri çek
        this.isFirstLoad = true;
        this.fetchData().then(() => {
          this.isFirstLoad = false;
        });
      }
    },

    // İlk yükleme
    async fetchData() {
      this.dashboardLoading = true;
      try {
        await this.$store.dispatch('dashboard/fetchDashboardData');
        await this.loadModuleList();
      } finally {
        this.dashboardLoading = false;
        this.isLoading = false;
      }
    },

    // Arka plan güncelleme
    async refreshDataInBackground() {
      await this.$store.dispatch('dashboard/fetchDashboardData');
      await this.loadModuleList();
    },

    // ModuleList yükle ve modül verilerini çek
    async loadModuleList() {
      try {
        const result = await this.$store.dispatch('dashboard/fetchModuleList', this.authUser.company_id);
        if (result?.status) {
          ls.set("dashboardMenuList", result.data);
          this.setupDashboardModules();
          // Tüm modül verileri yüklenene kadar bekle
          await this.fetchModuleData();
        }
      } catch (error) {
        console.error('Module list fetch error:', error);
      }
    },

    // Modül ayarlarını yap (modül listesi verilmezse store'daki kullanılır)
    setupDashboardModules(dashboardModules = null) {
      const dashboard = dashboardModules ?? this.moduleList?.dashboard ?? [];
      const leaderTable = dashboard.find(el => el.type === 'leader_table') || {};
      const socialWall = dashboard.find(el => el.type === 'social_wall') || {};

      this.leaderTableVisible = leaderTable.status === 1;
      this.socialWallVisible = socialWall.status === 1;
      this.socialWallTitle = this.getLocalizedWidgetTitle(socialWall, this.$t('home.social_wall'));
      this.leaderBoardTitle = this.getLocalizedWidgetTitle(leaderTable, this.$t('home.leaderboards'));
    },

    // Aktif modüller için verileri çek
    async fetchModuleData() {
      const promises = [];
      const searchFilter = { search: this.filters.search };

      // Her modül tipi için ilgili store action'ını çağır
      this.sortedModuleList.forEach(module => {
        switch (module.type) {
            // Eğitimler
          case 'upcoming_trainings':
            promises.push(this.$store.dispatch('dashboard/fetchAssignedEducations', searchFilter));
            break;
          case 'highlights_trainings':
            promises.push(this.$store.dispatch('dashboard/fetchHighlightEducations', searchFilter));
            break;
          case 'most_liked_trainings':
            promises.push(this.$store.dispatch('dashboard/fetchPopularEducations', searchFilter));
            break;
          case 'expired_trainings':
            promises.push(this.$store.dispatch('dashboard/fetchExpiredEducations', searchFilter));
            break;
          case 'new_trainings':
            promises.push(this.$store.dispatch('dashboard/fetchNewTrainings', searchFilter));
            break;
          case 'optional_trainings':
            promises.push(this.$store.dispatch('dashboard/fetchOptionalTrainings'));
            break;
          case 'competency_trainings':
            promises.push(this.$store.dispatch('dashboard/fetchCompetencyEducations', searchFilter));
            break;
          case 'collections':
            promises.push(this.$store.dispatch('dashboard/fetchCollections'));
            break;
          case 'my_instructor':
            promises.push(this.$store.dispatch('dashboard/fetchMyInstructors'));
            break;

            // Provider içerikleri
          case 'linkedin-learning':
            promises.push(this.$store.dispatch('dashboard/fetchLinkedinContents'));
            break;
          case 'udemy-business':
            promises.push(this.$store.dispatch('dashboard/fetchUdemyContents'));
            break;
          case 'harvard-business':
            promises.push(this.$store.dispatch('dashboard/fetchHarvardContents'));
            break;
          case 'aeroclass':
            promises.push(this.$store.dispatch('dashboard/fetchAeroclassContents'));
            break;
          case 'coursera':
            promises.push(this.$store.dispatch('dashboard/fetchCourseraContents'));
            break;

            // Custom widget — widget_id ile assignment çek
          default:
            if (module.id) {
              promises.push(this.$store.dispatch('dashboard/fetchWidgetAssignments', module.id));
            }
            break;
        }
      });

      // Sosyal widget'lar
      if (this.leaderTableVisible) {
        promises.push(this.$store.dispatch('dashboard/fetchLeaderBoard'));
      }
      if (this.socialWallVisible) {
        promises.push(this.$store.dispatch('dashboard/fetchLastPosts'));
      }

      await Promise.allSettled(promises);
    },
  },
  created() {
    // İlk render'dan önce cache'deki görünürlük/başlıkları uygula
    const cachedModules = getCachedDashboardModules();
    if (cachedModules) this.setupDashboardModules(cachedModules);
  },
  mounted() {
    this.initializePage();
  },
  watch: {
    '$i18n.locale'() {
      this.setupDashboardModules();
    }
  },
};
</script>

<style lang="scss" src="./HomePage.scss"></style>
